import React from 'react'
import { useTranslation } from 'react-i18next'
import { Paper, Typography, Box, Chip, Stack, List, ListItem, ListItemText, Divider, Button, CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import { FormSubmission, FormFieldType, FormSubmissionStatus, FormField } from '@/core/models/form.model'
import { PictureAsPdf } from '@mui/icons-material'

interface ViewFormSubmissionProps {
  submission: FormSubmission
  onGeneratePdf?: () => void
  isGeneratingPdf?: boolean
}

const ViewFormSubmissionComponent: React.FC<ViewFormSubmissionProps> = ({ submission, onGeneratePdf, isGeneratingPdf }) => {
  const { t } = useTranslation()

  const formatValue = (value: any, type: FormFieldType): string => {
    if (value === null || value === undefined) return '-'

    switch (type) {
      case FormFieldType.Boolean:
        return value ? t('pages.forms.fields.yes') : t('pages.forms.fields.no')
      case FormFieldType.Number:
        return value.toString()
      case FormFieldType.Date:
        return dayjs(value).format('YYYY-MM-DD')
      case FormFieldType.DateTime:
        return dayjs(value).format('YYYY-MM-DD HH:mm')
      default:
        return value
    }
  }

  const getFieldResponse = (fieldId: string): string => {
    const field = submission.template.fields.find((field: FormField) => field.id === fieldId)
    if (!field) return '-'
    return formatValue(submission.responses.find((r) => r.fieldId === fieldId)?.value, field.type as FormFieldType)
  }

  const canGeneratePdf = submission.template.aiOutputConfig?.enabled && submission.template.fillablePdf

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" gutterBottom>
              {submission.template.title}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {submission.template.description}
            </Typography>
          </Box>
          {canGeneratePdf && onGeneratePdf && (
            <Button
              variant="outlined"
              startIcon={isGeneratingPdf ? <CircularProgress size={20} /> : <PictureAsPdf />}
              onClick={onGeneratePdf}
              disabled={isGeneratingPdf}
            >
              {t('page.forms.submission.generatePdf', 'Generate PDF')}
            </Button>
          )}
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={t(`page.forms.submission.status.${submission.status.toLowerCase()}`)}
            color={submission.status === FormSubmissionStatus.Draft ? 'warning' : 'success'}
          />
          <Typography variant="body2" color="textSecondary">
            {t('page.forms.submission.lastModified', {
              date: dayjs(submission.updatedAt).format('YYYY-MM-DD HH:mm'),
            })}
          </Typography>
        </Stack>

        {submission.status === FormSubmissionStatus.Submitted && (
          <Box>
            <Typography variant="body2" color="textSecondary">
              {t('page.forms.submission.submittedBy', {
                name: `${submission.submittedBy.userInfo.name} ${submission.submittedBy.userInfo.surname}`,
              })}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('page.forms.submission.submittedAt', {
                date: submission.submittedAt ? dayjs(submission.submittedAt).format('YYYY-MM-DD HH:mm') : '-',
              })}
            </Typography>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {t('page.forms.submission.responses')}
          </Typography>
          <List>
            {submission.template.fields.map((field: FormField, index: number) => (
              <React.Fragment key={field.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText primary={field.label} secondary={getFieldResponse(field.id)} />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Stack>
    </Paper>
  )
}

export default ViewFormSubmissionComponent
